import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CorrectableField,
  FaxDenialReason,
  FaxLetterCategory,
  FaxLetterDecision,
  FaxLetterDetailsResponse,
} from "../../../api/postCall/faxLetter";
import { ApprovalStatus } from "../../authorisationDetails/utils/constants";
import { getAuthorizationFields } from "../utils/faxLetterCategory";

/** Working values of the Authorization section, every field as a string ("" when empty). */
export type AuthorizationValues = Record<CorrectableField, string>;

const FIELDS: CorrectableField[] = [
  "category",
  "reference_number",
  "start_date",
  "end_date",
  "num_visits",
  "quantity",
  "denial_reason",
  "denial_reason_category",
  "notes",
];

const toValues = (
  source: Partial<Record<CorrectableField, string | null | undefined>>,
): AuthorizationValues =>
  Object.fromEntries(
    FIELDS.map((field) => [field, (source[field] ?? "").trim()]),
  ) as AuthorizationValues;

export interface FaxLetterReviewState {
  /** "valid" (Approve), "invalid" (Reject), or null (no selection). */
  decision: ApprovalStatus;
  /** Current values: the letter as read, plus the reviewer's edits. */
  values: AuthorizationValues;
  /** The letter as read by the categorization layer. */
  original: AuthorizationValues;
  /** Fields shown for the current category whose value differs from the letter as read. */
  changedFields: CorrectableField[];
  /** Save is allowed: Approve, or Reject with at least one fix. */
  canSave: boolean;
  additionalComments: string;
  setAdditionalComments: (value: string) => void;
  validationErrors: Partial<Record<CorrectableField, string>>;
  setDecision: (decision: ApprovalStatus) => void;
  setField: (field: CorrectableField, value: string) => void;
  /** Cancel: clears the Approve/Reject selection and every edit. */
  reset: () => void;
  /** The corrections payload for a rejection (changed fields only, "" sent as null). */
  corrections: () => Partial<Record<CorrectableField, string | null>>;
}

/**
 * Approve / Reject / edit state for a fax letter's Authorization section.
 *
 * Opens with the saved review when the letter was reviewed before (its decision and corrected values),
 * else with no selection and the letter as read. Approving drops any edits: an approval confirms the
 * letter as read.
 *
 * @param details - The loaded details page data, or null while loading
 * @returns Review state and actions
 */
export const useFaxLetterReview = (
  details: FaxLetterDetailsResponse | null,
): FaxLetterReviewState => {
  const original = useMemo(
    () =>
      toValues({
        ...details?.authorization,
        denial_reason_category:
          details?.authorization.category === FaxLetterCategory.Denial
            ? details.authorization.denial_reason_category ||
              FaxDenialReason.Other
            : "",
      }),
    [details],
  );
  const [decision, setDecisionState] = useState<ApprovalStatus>(null);
  const [values, setValues] = useState<AuthorizationValues>(original);
  const [additionalComments, setAdditionalComments] = useState("");

  useEffect(() => {
    const review = details?.review;
    setAdditionalComments(review?.additional_comments ?? "");
    setDecisionState(
      review
        ? review.decision === FaxLetterDecision.Approved
          ? "valid"
          : "invalid"
        : null,
    );
    setValues(
      review
        ? { ...original, ...toValues({ ...original, ...review.corrections }) }
        : original,
    );
  }, [details, original]);

  const changedFields = useMemo(() => {
    const shown: CorrectableField[] = [
      "category",
      ...getAuthorizationFields(values.category as FaxLetterCategory).map(
        (field) => field.key,
      ),
    ];
    return shown.filter(
      (field) =>
        (field !== "denial_reason" ||
          values.denial_reason_category === FaxDenialReason.Other) &&
        values[field].trim() !== original[field],
    );
  }, [values, original]);

  const setDecision = useCallback(
    (next: ApprovalStatus) => {
      setDecisionState(next);
      if (next === "valid") setValues(original);
    },
    [original],
  );

  const setField = useCallback((field: CorrectableField, value: string) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "category" &&
      value === FaxLetterCategory.Denial &&
      !prev.denial_reason_category
        ? { denial_reason_category: FaxDenialReason.Other }
        : {}),
    }));
  }, []);

  const reset = useCallback(() => {
    setDecisionState(null);
    setValues(original);
    setAdditionalComments(details?.review?.additional_comments ?? "");
  }, [original, details]);

  const corrections = useCallback(
    () =>
      Object.fromEntries(
        changedFields.map((field) => [field, values[field].trim() || null]),
      ),
    [changedFields, values],
  );

  const validationErrors =
    values.category === FaxLetterCategory.Approval
      ? Object.fromEntries(
          Object.entries(details?.validation_errors ?? {}).filter(([field]) => {
            if (
              values[field as CorrectableField].trim() !==
              original[field as CorrectableField]
            )
              return false;
            // A reversed range is reported on End Date, but editing either boundary can repair
            // it. Invalid date text still keeps its own error; the server validates every edit.
            if (
              field === "end_date" &&
              values.start_date.trim() !== original.start_date
            ) {
              const start = moment(original.start_date, "MM/DD/YYYY", true);
              const end = moment(original.end_date, "MM/DD/YYYY", true);
              if (start.isValid() && end.isValid() && start.isAfter(end))
                return false;
            }
            return true;
          }),
        )
      : {};
  const needsSpecificReason =
    values.category === FaxLetterCategory.Denial &&
    values.denial_reason_category === FaxDenialReason.Other &&
    !values.denial_reason.trim();

  return {
    decision,
    values,
    original,
    changedFields,
    canSave:
      !needsSpecificReason &&
      Object.keys(validationErrors).length === 0 &&
      (decision === "valid" ||
        (decision === "invalid" && changedFields.length > 0)),
    additionalComments,
    setAdditionalComments,
    validationErrors,
    setDecision,
    setField,
    reset,
    corrections,
  };
};
